import { Injectable } from '@angular/core';
import { FiVimeoService } from '@fi-shared/media-bank/fi-vimeo/fi-vimeo.service';
import { ReplaySubject } from 'rxjs';
import { FiVimeoPreviewPicture } from '@fi-shared/media-bank/fi-vimeo/fi-vimeo';

export enum FiVimeoVideoStatus {
    Ok = 1,
    Processing = 2,
    VimeoError = 3,
}

export interface IVimeoDataItem {
    status: FiVimeoVideoStatus;
    height?: number;
    width?: number;
    previewPictures?: FiVimeoPreviewPicture[];
}

const CHECK_VIMEO_AVAILABILITY_INTERVAL = 15000;
const CHECK_VIMEO_ERROR_INTERVAL = 60000;
const CHECK_VIMEO_PREVIEW_IMAGE_MAX_COUNT = 3;

@Injectable()
export class FiVimeoDataService {
    private data: {
        [vimeoId: string]: ReplaySubject<IVimeoDataItem>;
    } = {};

    constructor(private vimeoService: FiVimeoService) {}

    getVimeoData$(vimeoId: string) {
        if (!this.data[vimeoId]) {
            // we have no data for given vimeo id sho should generate it
            this.data[vimeoId] = new ReplaySubject<IVimeoDataItem>(1);
            this.runCheckVideoAvailable(
                vimeoId,
                (dataItem) => {
                    this.data[vimeoId].next(dataItem);
                },
                true,
            );
        }
        return this.data[vimeoId];
    }

    private runCheckVideoAvailable(
        videoId: string,
        statusChangedFn: (dataItem: IVimeoDataItem) => void,
        firstRun?: boolean,
        previewImageCheckCounter: number = 0,
    ) {
        // from start current vimeo status can be "processing", in that case we should check availability recursively
        this.vimeoService.getVideoInfo(videoId).then(
            (videoInfo) => {
                if (videoInfo.isAvailable /* && !firstRun for tests*/) {
                    if (
                        videoInfo.previewPictures[0].link.includes('video/default_') &&
                        previewImageCheckCounter < CHECK_VIMEO_PREVIEW_IMAGE_MAX_COUNT
                    ) {
                        setTimeout(() => {
                            this.runCheckVideoAvailable(
                                videoId,
                                statusChangedFn,
                                undefined,
                                previewImageCheckCounter + 1,
                            );
                        }, CHECK_VIMEO_AVAILABILITY_INTERVAL);
                    } else {
                        statusChangedFn({
                            status: FiVimeoVideoStatus.Ok,
                            height: videoInfo.height,
                            width: videoInfo.width,
                            previewPictures: videoInfo.previewPictures,
                        });
                    }
                } else {
                    // if video is processing and first check finished - we should inform subscribers about current status (FiVimeoVideoStatus.Processing,)
                    if (firstRun) {
                        statusChangedFn({
                            status: FiVimeoVideoStatus.Processing,
                        });
                    }
                    setTimeout(() => {
                        this.runCheckVideoAvailable(videoId, statusChangedFn);
                    }, CHECK_VIMEO_AVAILABILITY_INTERVAL);
                }
            },
            () => {
                // something wrong on vimeo side
                statusChangedFn({
                    status: FiVimeoVideoStatus.VimeoError,
                });
                // retry after 60 seconds
                setTimeout(() => {
                    this.runCheckVideoAvailable(videoId, statusChangedFn);
                }, CHECK_VIMEO_ERROR_INTERVAL);
            },
        );
    }
}
